import type {BlockContext} from 'quickstart/types'
import * as React from 'react'
import {defaults, logger} from 'tizra'

const log = logger('quickstart/hooks/useBlockContext')

export const defaultBlockContext: BlockContext = {
  debugFlags: '',
  hacks: [],
  metaObject: null,
  metaType: '',
  pubType: 'LIVE',
  globalConfig: {},
  quickSearchFields: defaults.quickSearchFields,
  renderedAt: '',
  successUrl: '',
  tizraId: '',
  userAgent: '',
}

const BC = React.createContext<BlockContext>(defaultBlockContext)

export const BlockContextProvider = ({
  context,
  children,
}: {
  context: BlockContext
  children: React.ReactNode
}) => {
  const value = React.useMemo<BlockContext>(() => {
    log.debug?.('BlockContextProvider', context)
    if (!context) {
      log.error(`BlockContextProvider received ${context}`)
      return defaultBlockContext
    }
    const miss = Object.keys(defaultBlockContext).filter(k => !(k in context))
    const extra = Object.keys(context).filter(k => !(k in defaultBlockContext))
    if (miss.length) {
      if (
        miss.length === Object.keys(defaultBlockContext).length &&
        extra.length === 0
      ) {
        log.error('BlockContextProvider received empty object')
      } else {
        log.error(
          'BlockContextProvider received missing keys: ' +
            miss.sort().join(' '),
        )
      }
    }
    if (extra.length) {
      log.warn(
        'BlockContextProvider received extra keys: ' + extra.sort().join(' '),
      )
    }
    return miss.length ? {...defaultBlockContext, ...context} : context
  }, [context])
  return <BC.Provider value={value}>{children}</BC.Provider>
}

export const useBlockContext = (): BlockContext => React.useContext(BC)
